import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  BookOpen, ArrowRight, ChevronRight, Filter, 
  GraduationCap, Building2, Shield, Briefcase, School, Users, Sparkles
} from 'lucide-react';

// Program utama yang konsisten di seluruh aplikasi
const PROGRAMS = [
  {
    id: 'UTBK-SNBT',
    name: 'UTBK-SNBT',
    description: 'Persiapan tes masuk Perguruan Tinggi Negeri dengan materi TPS dan TKA lengkap',
    icon: GraduationCap,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    subjects: ['Penalaran Umum', 'Pengetahuan dan Pemahaman Umum', 'Pemahaman Bacaan dan Menulis', 'Pengetahuan Kuantitatif', 'Literasi Bahasa Indonesia', 'Literasi Bahasa Inggris', 'Penalaran Matematika'],
    features: ['Materi TPS Lengkap', 'Latihan TKA', 'Simulasi UTBK', 'Pembahasan Detail'],
    image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400&q=80'
  },
  {
    id: 'CPNS',
    name: 'CPNS',
    description: 'Persiapan seleksi Calon Pegawai Negeri Sipil dengan materi TWK, TIU, dan TKP',
    icon: Building2,
    color: 'from-emerald-500 to-emerald-600',
    bgColor: 'bg-emerald-50',
    subjects: ['Tes Wawasan Kebangsaan', 'Tes Intelegensi Umum', 'Tes Karakteristik Pribadi'],
    features: ['Materi TWK Lengkap', 'Latihan TIU', 'Strategi TKP', 'Passing Grade'],
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&q=80'
  },
  {
    id: 'TNI',
    name: 'TNI',
    description: 'Persiapan seleksi AD, AL, AU untuk Tamtama, Bintara, dan Akmil',
    icon: Shield,
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-50',
    subjects: ['TWK', 'TIU', 'TKP', 'Psikotes', 'Tes Kesehatan', 'Tes Jasmani'],
    features: ['Tes Akademik TNI', 'Psikotes Militer', 'Tes Kesehatan', 'Tes Jasmani'],
    image: 'https://images.unsplash.com/photo-1579912437766-7896df6d3cd3?w=400&q=80'
  },
  {
    id: 'POLRI',
    name: 'POLRI',
    description: 'Persiapan seleksi Tamtama, Bintara, Akpol, dan SIPSS Polri',
    icon: Shield,
    color: 'from-amber-500 to-amber-600',
    bgColor: 'bg-amber-50',
    subjects: ['Tes Akademik', 'Psikotes Polri', 'Tes Kesehatan', 'Tes Jasmani'],
    features: ['Tes Akademik', 'Psikotes Polri', 'Tes Kesehatan', 'Tes Jasmani'],
    image: 'https://images.unsplash.com/photo-1604881991720-f91add269bed?w=400&q=80'
  },
  {
    id: 'SEKOLAH KEDINASAN',
    name: 'Sekolah Kedinasan',
    description: 'Persiapan seleksi STAN, STIS, IPDN, STTD, dan sekolah kedinasan lainnya',
    icon: School,
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50',
    subjects: ['TWK', 'TIU', 'TKP', 'Tes Bidang'],
    features: ['Materi SKD', 'Tes Bidang Khusus', 'Simulasi SKB', 'Tips Lolos'],
    image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=400&q=80'
  },
  {
    id: 'BUMN',
    name: 'BUMN',
    description: 'Persiapan seleksi rekrutmen BUMN dengan tes kompetensi dasar dan bidang',
    icon: Briefcase,
    color: 'from-cyan-500 to-cyan-600',
    bgColor: 'bg-cyan-50',
    subjects: ['TWK', 'TIU', 'TKP', 'Core Values BUMN', 'Tes Bidang'],
    features: ['Core Values AKHLAK', 'Tes Kompetensi', 'Studi Kasus', 'Wawancara'],
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&q=80'
  },
  {
    id: 'TKA',
    name: 'TKA (Tes Kemampuan Akademik)',
    description: 'Persiapan Tes Kemampuan Akademik untuk berbagai keperluan seleksi',
    icon: BookOpen,
    color: 'from-indigo-500 to-indigo-600',
    bgColor: 'bg-indigo-50',
    subjects: ['Matematika', 'Fisika', 'Kimia', 'Biologi', 'Ekonomi', 'Geografi', 'Sejarah', 'Sosiologi'],
    features: ['Materi Sains', 'Materi Sosial', 'Latihan Soal', 'Pembahasan'],
    image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&q=80'
  },
  {
    id: 'SMA Favorit',
    name: 'SMA Unggul/Favorit',
    description: 'Persiapan masuk SMA favorit dan sekolah unggulan',
    icon: Users,
    color: 'from-pink-500 to-pink-600',
    bgColor: 'bg-pink-50',
    subjects: ['Matematika', 'Bahasa Indonesia', 'Bahasa Inggris', 'IPA Terpadu'],
    features: ['Matematika', 'Bahasa Indonesia', 'Bahasa Inggris', 'IPA'],
    image: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=400&q=80'
  }
];

export default function ProgramsPage() {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');

  // Filter programs
  const filteredPrograms = filter === 'all' 
    ? PROGRAMS 
    : PROGRAMS.filter(p => p.id === filter);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="programs-page">
      {/* Header with Mobile Navigation */}
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-r from-[#4472C4] via-[#5a8dd6] to-[#4472C4] text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 mb-4 px-4 py-1.5">
            <Sparkles className="w-3 h-3 mr-1.5" />
            {PROGRAMS.length} Program Tersedia
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Pilih <span className="text-yellow-300">Program</span> yang Sesuai
          </h1>
          <p className="text-lg opacity-90 max-w-2xl mx-auto">
            Program belajar lengkap untuk berbagai tes dan ujian dengan tentor berpengalaman
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-6 px-4 bg-white border-b sticky top-[57px] z-40 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-slate-400" />
            <span className="text-sm font-medium text-slate-600">Filter Program:</span>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[220px] bg-slate-50" data-testid="program-filter">
                <SelectValue placeholder="Semua Program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Program</SelectItem>
                {PROGRAMS.map(prog => (
                  <SelectItem key={prog.id} value={prog.id}>{prog.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-slate-500">
            {filteredPrograms.length} program ditemukan
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredPrograms.map((prog) => {
              const IconComponent = prog.icon;
              return (
                <Card 
                  key={prog.id} 
                  className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-slate-200"
                  data-testid={`program-card-${prog.id}`}
                >
                  {/* Image */}
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={prog.image} 
                      alt={prog.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${prog.color} opacity-60`} />
                    <div className="absolute top-3 left-3">
                      <Badge className="bg-white/90 text-slate-700">{prog.name}</Badge>
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <div className="w-10 h-10 bg-white/90 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-slate-700" />
                      </div>
                    </div>
                  </div>

                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg group-hover:text-[#4472C4] transition-colors">
                      {prog.name}
                    </CardTitle>
                    <CardDescription className="line-clamp-2">
                      {prog.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pb-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1.5">Mata Pelajaran:</p>
                        <div className="flex flex-wrap gap-1">
                          {prog.subjects.slice(0, 3).map((subj, i) => (
                            <Badge key={i} variant="secondary" className="text-xs bg-slate-100">
                              {subj}
                            </Badge>
                          ))}
                          {prog.subjects.length > 3 && (
                            <Badge variant="secondary" className="text-xs bg-slate-100">
                              +{prog.subjects.length - 3}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0">
                    <Link to={`/packages?program=${prog.id}`} className="w-full">
                      <Button className={`w-full bg-gradient-to-r ${prog.color} text-white hover:opacity-90 shadow-md`}>
                        Lihat Paket
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF6B35] to-[#ff8555]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Tidak yakin program mana?</h2>
          <p className="text-lg opacity-90 mb-8">
            Konsultasikan kebutuhanmu dengan tim kami untuk rekomendasi terbaik
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/packages">
              <Button className="bg-white text-[#FF6B35] hover:bg-slate-100 px-8 py-6 text-lg rounded-xl shadow-lg">
                Lihat Semua Paket
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="https://wa.me/6281262017001?text=Halo,%20saya%20ingin%20konsultasi%20tentang%20program%20bimbel%20MyTentor" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl">
                Konsultasi WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
